import { DateColDefTimeSettings } from './date';
import { ColDef, IRowNode, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { DateTime } from 'luxon';
import { NO_VALUE } from '../../../Ticker';
import { getCorrectDateFormat } from './getCorrectDateFormat';
import { TimeFilter } from './TimeFilter';

const getTimeValue = (time: string): number => {
  const pmAddendum = time.toUpperCase().includes('PM') ? 120000 : 0;
  return +time.replaceAll(/:|AM|PM|am|pm|\s/g, '') + pmAddendum;
};

const dateTimeComparator =
  (dateField = 'date', timeField = 'time', zone = 'utc') =>
  (_value1: string, _value2: string, node1: IRowNode, node2: IRowNode) => {
    const node1Date = node1.data[dateField];
    const node2Date = node2.data[dateField];
    const node1Time = typeof node1.data[timeField] === 'string' ? node1.data[timeField] : '';
    const node2Time = typeof node2.data[timeField] === 'string' ? node2.data[timeField] : '';

    if (!node1Date && node2Date) return -1;
    if (!node2Date && node1Date) return 1;
    if (!node1Date && !node2Date) {
      return getTimeValue(node1Time) - getTimeValue(node2Time);
    }

    const dt1 = DateTime.fromISO(`${node1Date}T${node1Time || '00:00'}`, { zone });
    const dt2 = DateTime.fromISO(`${node2Date}T${node2Time || '00:00'}`, { zone });

    return dt1.toMillis() - dt2.toMillis();
  };

export const DateTimeColDef = (
  dateColDef: ColDef,
  timeColDef: ColDef,
  settings: DateColDefTimeSettings,
): [ColDef, ColDef] => {
  const timezone = settings.timezone ?? 'utc';
  return [
    {
      cellStyle: { textAlign: 'left' },
      comparator: dateTimeComparator(dateColDef.field, timeColDef.field),
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['clear'],
        comparator(filterDateValue: Date, cellDateValue: Date | string): number {
          const filterDt = DateTime.fromJSDate(filterDateValue, { zone: timezone }).startOf('day');

          let cellDt: DateTime;
          if (typeof cellDateValue === 'string') {
            cellDt = DateTime.fromISO(cellDateValue, { zone: timezone }).startOf('day');
          } else {
            cellDt = DateTime.fromJSDate(cellDateValue as Date, { zone: timezone }).startOf('day');
          }

          if (cellDt.hasSame(filterDt, 'day')) return 0;
          return cellDt < filterDt ? -1 : 1;
        },
      },

      valueFormatter: (params: ValueFormatterParams) => {
        const dateValue = params.value;
        if (!dateValue) return NO_VALUE;
        const format = getCorrectDateFormat(dateValue);
        const originalDate = DateTime.fromISO(dateValue, { zone: 'America/New_York' });
        const date = originalDate.toFormat(format);
        return date !== 'Invalid DateTime' ? date : dateValue;
      },

      ...dateColDef,
    },
    {
      cellStyle: { textAlign: 'left' },
      comparator: dateTimeComparator(dateColDef.field, timeColDef.field),
      filter: TimeFilter,
      filterParams: {
        column: timeColDef.field,
      },
      valueFormatter: ({ value }: ValueFormatterParams): string => value ?? '-',
      valueGetter: ({ colDef: { field = 'time' }, data }: ValueGetterParams) => {
        const value = data[field];
        if (!value) return value;
        const dt = DateTime.fromISO(`2000-01-01T${value}`, { zone: 'America/New_York' }).setZone(timezone);
        if (settings.timeFormat === 'AM/PM') return dt.toFormat('hh:mm:ss a');
        return dt.toFormat('HH:mm:ss');
      },
      ...timeColDef,
    },
  ];
};
